import React, { useState, useEffect } from 'react';
import { getDatabase, ref, set, onValue, query, orderByChild, equalTo } from "firebase/database";
import { getAuth } from 'firebase/auth';
import { useNavigate } from 'react-router-dom';
import DashboardHeader from '../../AdminComponents/DashboardHeader';
import GenerativeOrbAnimation from '../../Pages/GenerativeOrbAnimation';
import ProductCreator from '../Components/ProductCreator';
import ProductsList from '../Components/ProductsList';
import './ProductDashboard.css';

const ProductDashboard = () => {
    const [products, setProducts] = useState([]);
    const [showCreateModal, setShowCreateModal] = useState(false);
    const [isProvider, setIsProvider] = useState(false);
    const [loading, setLoading] = useState(true);
    const [salesSettings, setSalesSettings] = useState(null);
    const [selectedProductType, setSelectedProductType] = useState(null);

    const db = getDatabase();
    const auth = getAuth();
    const currentUser = auth.currentUser;
    const navigate = useNavigate();

    // Check if user is a provider and load settings
    useEffect(() => {
        if (currentUser) {
            const userRef = ref(db, `Users/${currentUser.uid}`);
            onValue(userRef, (snapshot) => {
                if (snapshot.exists()) {
                    const userData = snapshot.val();
                    const settings = userData.salesSettings;
                    
                    if (settings && settings.isProvider) {
                        setIsProvider(true);
                        setSalesSettings(settings);
                    } else {
                        // Automatisch Provider aktivieren für neue Benutzer
                        handleEnableProvider();
                    }
                } else {
                    // Automatisch Provider aktivieren für neue Benutzer
                    handleEnableProvider();
                }
                setLoading(false);
            });
        }
    }, [currentUser, db]);

    // Load user's products
    useEffect(() => {
        if (currentUser && isProvider) {
            const productsQuery = query(
                ref(db, 'products'),
                orderByChild('providerId'),
                equalTo(currentUser.uid)
            );

            onValue(productsQuery, (snapshot) => {
                if (snapshot.exists()) {
                    const productsData = [];
                    snapshot.forEach((childSnapshot) => {
                        productsData.push({
                            id: childSnapshot.key,
                            ...childSnapshot.val()
                        });
                    });
                    const sortedProducts = productsData.sort((a, b) => b.createdAt - a.createdAt);
                    setProducts(sortedProducts);
                    // Type Selector wird nie mehr angezeigt
                } else {
                    setProducts([]);
                }
            });
        }
    }, [currentUser, isProvider, db]);

    const handleEnableProvider = async () => {
        if (!currentUser) return;

        try {
            await set(ref(db, `Users/${currentUser.uid}/salesSettings`), {
                isProvider: true,
                businessName: 'My Business',
                allowStripePayments: true,
                allowCashPayments: true,
                allowFreeProducts: true,
                defaultCurrency: 'EUR',
                stripeConfigured: false
            });
            
            setIsProvider(true);
        } catch (error) {
            console.error('Error enabling provider:', error);
            alert('Error enabling sales features. Please try again.');
        }
    };

    const handleProductTypeSelection = (type) => {
        setSelectedProductType(type);
        setShowCreateModal(true);
    };

    const handleCreateProduct = () => {
        if (!isProvider) {
            // Enable provider first, then show quick create
            handleEnableProvider();
        }
        // Zeige immer die Quick Create Bar, nie den Type Selector
    };

    const handleProductCreated = (productId) => {
        setShowCreateModal(false);
        setSelectedProductType(null);
        // Navigate to product settings or refresh list
        console.log('Product created:', productId);
    };

    const handleBackToTypeSelector = () => {
        setShowCreateModal(false);
        setSelectedProductType(null);
        // Nicht mehr zum Type Selector zurück - bleibe im Dashboard
    };

    if (loading) {
        return (
            <div className="loading-spinner">
                <h1>BonusClub</h1>
                <div className="spinner-circle"></div>
            </div>
        );
    }


    return (
        <div>
            <DashboardHeader />
            <GenerativeOrbAnimation />
            
            <div className="sales-dashboard-container">
                {/* Immer das Provider Dashboard anzeigen, auch für neue Benutzer */}
                {/* Provider Dashboard - SalesGod AI Style */}
                    <div className="salesgod-provider-dashboard">
                        {/* SalesGod AI Header */}
                        <div className="salesgod-dashboard-header">
                            <div className="salesgod-ai-header-container">
                                <div className="salesgod-ai-icon-container">
                                    <img src="/img/salesgod_dark.webp" alt="SalesGod" className="salesgod-ai-brain-icon" style={{ width: '58px', height: '58px' }} />
                                    <div className="salesgod-ai-pulse-ring"></div>
                                </div>
                                <h2 className="salesgod-ai-title" style={{ marginBottom: '40px' }}>
                                    <span className="salesgod-sales-text">Your</span>
                                    <span className="salesgod-ai-text">
                                        Products & Courses
                                        <div className="salesgod-ai-glow"></div>
                                    </span>
                                </h2>
                              
                              
                            </div>
                        </div>

                        {/* Show Products First (if any exist) */}
                        {products.length > 0 && (
                            <ProductsList 
                                products={products}
                                onEditProduct={(productId) => navigate(`/sales/product/${productId}`)}
                                onDeleteProduct={(productId) => {
                                    // Implement delete functionality
                                    console.log('Delete product:', productId);
                                }}
                            />
                        )}

                        {/* Quick Create Bar - Expanded Inspirations */}
                        <div className="salesgod-quick-create-bar">
                            <h3 className="salesgod-inspirations-title">What would you like to sell?</h3>
                            <div className="salesgod-quick-create-buttons">
                                {/* 🔵 DIGITAL PRODUCTS - Lila/Blau Gruppe */}
                                <button 
                                    className="salesgod-quick-btn salesgod-quick-digital"
                                    onClick={() => handleProductTypeSelection('digital')}
                                >
                                    <div className="salesgod-quick-icon">
                                        <i className="fas fa-file-pdf"></i>
                                    </div>
                                    <h4 className="salesgod-quick-title">E-Books & PDFs</h4>
                                    <p className="salesgod-quick-description">Guides, workbooks, checklists, templates</p>
                                </button>
                                
                                <button 
                                    className="salesgod-quick-btn salesgod-quick-digital"
                                    onClick={() => handleProductTypeSelection('digital')}
                                >
                                    <div className="salesgod-quick-icon">
                                        <i className="fas fa-palette"></i>
                                    </div>
                                    <h4 className="salesgod-quick-title">Design Templates</h4>
                                    <p className="salesgod-quick-description">Canva templates, social media packs, logos</p>
                                </button>
                                
                                <button 
                                    className="salesgod-quick-btn salesgod-quick-digital"
                                    onClick={() => handleProductTypeSelection('digital')}
                                >
                                    <div className="salesgod-quick-icon">
                                        <i className="fas fa-code"></i>
                                    </div>
                                    <h4 className="salesgod-quick-title">Digital Tools</h4>
                                    <p className="salesgod-quick-description">Software, apps, plugins, digital tools</p>
                                </button>
                                
                                <button 
                                    className="salesgod-quick-btn salesgod-quick-digital"
                                    onClick={() => handleProductTypeSelection('digital')}
                                >
                                    <div className="salesgod-quick-icon">
                                        <i className="fas fa-clipboard-list"></i>
                                    </div>
                                    <h4 className="salesgod-quick-title">Assessment & Quiz</h4>
                                    <p className="salesgod-quick-description">Personality tests, skill assessments, evaluations</p>
                                </button>
                                
                                <button 
                                    className="salesgod-quick-btn salesgod-quick-digital"
                                    onClick={() => handleProductTypeSelection('digital')}
                                >
                                    <div className="salesgod-quick-icon">
                                        <i className="fas fa-calendar-check"></i>
                                    </div>
                                    <h4 className="salesgod-quick-title">Challenge Program</h4>
                                    <p className="salesgod-quick-description">30-day challenges, habit trackers, daily tasks</p>
                                </button>
                                
                                <button 
                                    className="salesgod-quick-btn salesgod-quick-digital"
                                    onClick={() => handleProductTypeSelection('digital')}
                                >
                                    <div className="salesgod-quick-icon">
                                        <i className="fas fa-award"></i>
                                    </div>
                                    <h4 className="salesgod-quick-title">Certification</h4>
                                    <p className="salesgod-quick-description">Professional certificates, badges, credentials</p>
                                </button>

                                {/* 🟢 ONLINE COURSES - Grün Gruppe */}
                                <button 
                                    className="salesgod-quick-btn salesgod-quick-course"
                                    onClick={() => handleProductTypeSelection('course')}
                                >
                                    <div className="salesgod-quick-icon">
                                        <i className="fas fa-graduation-cap"></i>
                                    </div>
                                    <h4 className="salesgod-quick-title">Video Course</h4>
                                    <p className="salesgod-quick-description">Step-by-step learning with video modules</p>
                                </button>
                                
                                <button 
                                    className="salesgod-quick-btn salesgod-quick-course"
                                    onClick={() => handleProductTypeSelection('course')}
                                >
                                    <div className="salesgod-quick-icon">
                                        <i className="fas fa-dumbbell"></i>
                                    </div>
                                    <h4 className="salesgod-quick-title">Fitness Program</h4>
                                    <p className="salesgod-quick-description">Workout plans, nutrition guides, coaching</p>
                                </button>
                                
                                <button 
                                    className="salesgod-quick-btn salesgod-quick-course"
                                    onClick={() => handleProductTypeSelection('course')}
                                >
                                    <div className="salesgod-quick-icon">
                                        <i className="fas fa-chart-line"></i>
                                    </div>
                                    <h4 className="salesgod-quick-title">Business Coaching</h4>
                                    <p className="salesgod-quick-description">Masterclasses, business strategies, consulting</p>
                                </button>
                                
                                <button 
                                    className="salesgod-quick-btn salesgod-quick-course"
                                    onClick={() => handleProductTypeSelection('course')}
                                >
                                    <div className="salesgod-quick-icon">
                                        <i className="fas fa-users-cog"></i>
                                    </div>
                                    <h4 className="salesgod-quick-title">Group Coaching</h4>
                                    <p className="salesgod-quick-description">Mastermind groups, group calls, community access</p>
                                </button>

                                {/* 🟡 LIVE EVENTS - Orange Gruppe */}
                                <button 
                                    className="salesgod-quick-btn salesgod-quick-event"
                                    onClick={() => handleProductTypeSelection('event')}
                                >
                                    <div className="salesgod-quick-icon">
                                        <i className="fas fa-video"></i>
                                    </div>
                                    <h4 className="salesgod-quick-title">Live Webinar</h4>
                                    <p className="salesgod-quick-description">Interactive online sessions and Q&As</p>
                                </button>
                                
                                <button 
                                    className="salesgod-quick-btn salesgod-quick-event"
                                    onClick={() => handleProductTypeSelection('event')}
                                >
                                    <div className="salesgod-quick-icon">
                                        <i className="fas fa-users"></i>
                                    </div>
                                    <h4 className="salesgod-quick-title">Workshop</h4>
                                    <p className="salesgod-quick-description">Hands-on learning experiences and group sessions</p>
                                </button>
                                
                                <button 
                                    className="salesgod-quick-btn salesgod-quick-event"
                                    onClick={() => handleProductTypeSelection('event')}
                                >
                                    <div className="salesgod-quick-icon">
                                        <i className="fas fa-microphone"></i>
                                    </div>
                                    <h4 className="salesgod-quick-title">Conference</h4>
                                    <p className="salesgod-quick-description">Multi-day events, networking, speakers</p>
                                </button>
                                
                                <button 
                                    className="salesgod-quick-btn salesgod-quick-event"
                                    onClick={() => handleProductTypeSelection('event')}
                                >
                                    <div className="salesgod-quick-icon">
                                        <i className="fas fa-user-tie"></i>
                                    </div>
                                    <h4 className="salesgod-quick-title">One on One Session</h4>
                                    <p className="salesgod-quick-description">Personal coaching calls, consultations, mentoring</p>
                                </button>
                            </div>
                        </div>

                        {products.length === 0 && (
                            <div className="salesgod-empty-state">
                                <div className="salesgod-empty-icon-container">
                                    <div className="salesgod-empty-icon">
                                        <i className="fas fa-box-open"></i>
                                    </div>
                                    <div className="salesgod-empty-pulse-ring"></div>
                                </div>
                                <h3 className="salesgod-empty-title">No Products Yet</h3>
                                <p className="salesgod-empty-description">Create your first product to start selling with SalesGod AI</p>
                                <button 
                                    className="salesgod-create-first-btn"
                                    onClick={handleCreateProduct}
                                >
                                    <i className="fas fa-magic"></i>
                                    Create Your First Product
                                </button>
                            </div>
                        )}
                    </div>

                {showCreateModal && (
                    <ProductCreator 
                        onClose={handleBackToTypeSelector}
                        onProductCreated={handleProductCreated}
                        salesSettings={salesSettings}
                        preselectedType={selectedProductType}
                    />
                )}
            </div>
        </div>
    );
};

export default ProductDashboard;
